import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import Header from "@user/components/Header";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, Send, PhoneCall, Video, CheckCircle2, Download, MessageSquare,
  Star, Clock, Play, Sparkles, FileText, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import RatingPromptModal from "@user/components/RatingPromptModal";
import DailyCall from "@shared/consultation/DailyCall";
import { useConsultationSocket } from "@/hooks/useConsultationSocket";

export default function ConsultationRoom() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const invoiceNo = sp.get("invoice");
  const openReview = sp.get("review") === "1";
  const { user } = useAuth();
  const [consult, setConsult] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [inCall, setInCall] = useState(false);
  const [otherTyping, setOtherTyping] = useState(false);
  const [ratingOpen, setRatingOpen] = useState(openReview);
  const [hasReviewed, setHasReviewed] = useState(false);
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);
  const typingTimerRef = useRef(null);
  const otherTypingTimerRef = useRef(null);

  const isLawyerSide = user?.role === "lawyer" && consult && user?.lawyer_id === consult.lawyer_id;

  const load = useCallback(async () => {
    const r = await api.get(`/consultations/${id}`);
    setConsult(r.data);
  }, [id]);

  // Load history once
  const loadMessages = useCallback(async () => {
    try {
      const r = await api.get(`/consultations/${id}/messages`);
      setMessages(r.data.messages || []);
    } catch { /* ignore */ }
  }, [id]);

  useEffect(() => { load(); loadMessages(); }, [load, loadMessages]);

  // WebSocket — primary realtime channel.
  const { connected, send, addEventListener } = useConsultationSocket(id, { enabled: !!id });

  useEffect(() => {
    const offMsg = addEventListener("message", (evt) => {
      const m = evt.message;
      if (!m) return;
      setMessages((prev) => prev.some((x) => x.id === m.id) ? prev : [...prev, m]);
    });
    const offTyping = addEventListener("typing", (evt) => {
      // Show "other typing" only when peer is the other party
      const myRole = user?.role === "lawyer" && consult && user?.lawyer_id === consult.lawyer_id ? "lawyer" : "user";
      if (evt.role && evt.role === myRole) return;
      setOtherTyping(true);
      if (otherTypingTimerRef.current) clearTimeout(otherTypingTimerRef.current);
      otherTypingTimerRef.current = setTimeout(() => setOtherTyping(false), 4000);
    });
    const offStatus = addEventListener("status", () => { load(); });
    const offError = addEventListener("error", (evt) => { toast.error(evt.detail || "Channel error"); });
    return () => { offMsg(); offTyping(); offStatus(); offError(); };
  }, [addEventListener, load, user, consult]);

  // Polling fallback when WS not connected (covers ingress hiccups).
  useEffect(() => {
    if (connected) return;
    const iv = setInterval(loadMessages, 4000);
    return () => clearInterval(iv);
  }, [connected, loadMessages]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const sendMsg = async () => {
    const content = input.trim();
    if (!content) return;
    setInput("");
    if (connected && send({ type: "chat", content })) return;
    // Fallback to REST.
    try {
      const r = await api.post(`/consultations/${id}/messages`, { content });
      setMessages((m) => [...m, r.data]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    }
  };

  const onTyping = (v) => {
    setInput(v);
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      if (connected) send({ type: "typing" });
      else api.post(`/consultations/${id}/typing`).catch(() => {});
    }, 400);
  };

  // Auto-prompt rating
  useEffect(() => {
    if (!consult || !user) return;
    if (user.user_id !== consult.user_id) return;
    if (consult.status !== "completed") return;
    const dismissedKey = `adv_rated_${consult.id}`;
    if (hasReviewed || localStorage.getItem(dismissedKey)) return;
    api.get(`/consultations/${consult.id}/review`).then((r) => {
      if (!r.data?.rating) setRatingOpen(true);
      else setHasReviewed(true);
    }).catch(() => setRatingOpen(true));
  }, [consult, user, hasReviewed]);

  const startConsult = async () => {
    setBusy(true);
    try {
      await api.post(`/consultations/${id}/start`);
      toast.success("Consultation started — client notified");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start"); }
    finally { setBusy(false); }
  };
  const completeConsult = async () => {
    setBusy(true);
    try {
      await api.post(`/consultations/${id}/complete`);
      toast.success("Consultation marked complete");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not complete"); }
    finally { setBusy(false); }
  };

  if (!consult) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <div className="max-w-3xl mx-auto p-8 text-center text-slate-500">Loading consultation…</div>
      </div>
    );
  }

  const isLive = consult.status === "live";
  const isCompleted = consult.status === "completed";
  const canStart = isLawyerSide && consult.payment_status === "paid"
    && !isCompleted && !isLive;

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <Header />

      {invoiceNo && (
        <div className="bg-emerald-50 border-b border-emerald-200 px-4 py-2 text-center text-xs font-bold text-emerald-800 flex items-center justify-center gap-2" data-testid="payment-success-banner">
          <CheckCircle2 size={14} /> Payment successful · Invoice {invoiceNo}
          <button onClick={() => window.print()} className="ml-2 underline inline-flex items-center gap-1"><Download size={12} /> Print</button>
        </div>
      )}

      <div className="flex-1 max-w-5xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 grid lg:grid-cols-12 gap-6">
        {/* Info sidebar */}
        <aside className="lg:col-span-4 space-y-4">
          <Link to={isLawyerSide ? "/lawyer-portal/consultations" : "/dashboard"} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0A2540]">
            <ArrowLeft size={14} /> {isLawyerSide ? "All consultations" : "My consultations"}
          </Link>
          <div className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex items-center gap-3">
              {(isLawyerSide ? null : consult.lawyer_photo) ? (
                <img src={consult.lawyer_photo} alt="" className="h-12 w-12 rounded-xl object-cover" />
              ) : (
                <div className="h-12 w-12 rounded-xl adv-navy text-[#D4AF37] grid place-items-center font-bold">
                  {(isLawyerSide ? consult.user_name : consult.lawyer_name)?.split(" ").slice(-2).map((x) => x[0]).join("") || "C"}
                </div>
              )}
              <div>
                <p className="font-bold text-[#0A2540]" data-testid="consult-other-name">
                  {isLawyerSide ? consult.user_name : consult.lawyer_name}
                </p>
                <p className="text-xs text-slate-500 capitalize">{consult.consultation_type} consultation</p>
              </div>
            </div>
            <div className="mt-4 text-sm space-y-2">
              <Row k="Status" v={
                <span className={`capitalize font-bold ${isLive ? "text-emerald-600" : "text-[#0A2540]"}`} data-testid="consult-status">
                  {isLive && <span className="adv-pulse-dot inline-block mr-1" />}{consult.status}
                </span>
              } />
              <Row k="Payment" v={<span className={`capitalize font-bold ${consult.payment_status === "paid" ? "text-emerald-600" : "text-amber-700"}`}>{consult.payment_status}</span>} />
              <Row k="Amount" v={<span className="font-bold">₹{consult.amount}</span>} />
              {consult.slot_date && <Row k="Slot" v={`${consult.slot_date} · ${consult.slot_time}`} />}
              {consult.invoice_no && <Row k="Invoice" v={consult.invoice_no} />}
              <Row k="Channel" v={
                <span className="text-[10px] font-bold uppercase tracking-wider">
                  <span className={`inline-block h-1.5 w-1.5 rounded-full mr-1 ${connected ? "bg-emerald-500" : "bg-amber-500"}`} />
                  {connected ? "Live" : "Reconnecting…"}
                </span>
              } />
            </div>
            <div className="mt-4 pt-4 border-t border-slate-100">
              <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Query</p>
              <p className="text-sm text-slate-700 mt-1">{consult.query}</p>
            </div>

            {/* Lawyer controls */}
            {canStart && (
              <Button onClick={startConsult} disabled={busy} data-testid="start-consult-btn"
                className="mt-4 w-full rounded-xl font-bold h-11 adv-btn-gold">
                <Play size={16} className="mr-2" /> Start consultation
              </Button>
            )}

            {consult.payment_status === "paid" && consult.consultation_type !== "chat" && (
              <Button
                onClick={() => setInCall(true)}
                data-testid="start-call-btn"
                className={`mt-2 w-full rounded-xl font-bold h-11 ${consult.consultation_type === "video" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "adv-btn-gold"}`}
              >
                {consult.consultation_type === "video" ? <Video size={16} className="mr-2" /> : <PhoneCall size={16} className="mr-2" />}
                Join {consult.consultation_type} call
              </Button>
            )}
            {isLawyerSide && !isCompleted && (
              <Button onClick={completeConsult} variant="outline" disabled={busy} data-testid="complete-btn"
                className="mt-2 w-full rounded-xl font-bold">
                Mark complete
              </Button>
            )}
          </div>

          {/* AI summary panel */}
          {(isCompleted || isLawyerSide) && (
            <AiSummaryPanel cid={consult.id} canEdit={isLawyerSide} />
          )}
        </aside>

        {/* Chat area */}
        <section className="lg:col-span-8 flex flex-col bg-white border border-slate-200 rounded-2xl overflow-hidden min-h-[70vh]">
          <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <MessageSquare size={16} className="text-[#0A2540] shrink-0" />
              <p className="text-sm font-bold text-[#0A2540] truncate">
                Direct chat with {isLawyerSide ? consult.user_name : consult.lawyer_name}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {consult.consultation_type === "chat" && !isCompleted && consult.payment_status === "paid" && isLive && (
                <ChatSessionTimer durationMin={15} startedAt={consult.started_at} />
              )}
              <span className="text-[10px] font-bold flex items-center gap-1" data-testid="consult-channel-pill">
                <span className={`h-1.5 w-1.5 rounded-full ${connected ? "bg-emerald-500" : "bg-amber-500"} ${connected ? "animate-pulse" : ""}`} />
                {connected ? "Live" : "Polling"}
              </span>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-3" data-testid="consult-messages">
            {consult.payment_status !== "paid" && (
              <div className="max-w-md mx-auto text-center py-10">
                <Lock className="mx-auto text-slate-300" />
                <p className="mt-3 text-sm text-slate-500">Complete payment to start chatting with the lawyer.</p>
              </div>
            )}
            {!isLive && consult.payment_status === "paid" && !isCompleted && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-900 flex items-center gap-2" data-testid="consult-not-live-banner">
                <Clock size={14} /> {isLawyerSide
                  ? "Press Start consultation when ready — the timer begins for the client at that moment."
                  : "Waiting for the lawyer to start the consultation."}
              </div>
            )}
            {messages.map((m) => {
              const mine = m.sender_id === user?.user_id;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`cmsg-${mine ? "mine" : "theirs"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${mine ? "bg-[#0A2540] text-white rounded-tr-sm" : "bg-[#FBF4DF] text-[#0A2540] rounded-tl-sm"}`}>
                    <p className="whitespace-pre-wrap">{m.content}</p>
                    <p className={`text-[10px] mt-1 ${mine ? "text-white/50" : "text-slate-500"}`}>{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
                  </div>
                </div>
              );
            })}
            {otherTyping && (
              <div className="flex justify-start" data-testid="typing-indicator">
                <div className="bg-slate-100 rounded-2xl rounded-tl-sm px-4 py-3 flex gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-slate-500 animate-bounce" />
                  <span className="h-1.5 w-1.5 rounded-full bg-slate-500 animate-bounce" style={{ animationDelay: "120ms" }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-slate-500 animate-bounce" style={{ animationDelay: "240ms" }} />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {consult.payment_status === "paid" && !isCompleted && (
            <div className="border-t border-slate-100 p-3 md:p-4 bg-white">
              <div className="flex items-end gap-2">
                <Textarea
                  value={input}
                  onChange={(e) => onTyping(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMsg(); } }}
                  placeholder={isLive ? "Type a message…" : (isLawyerSide ? "Start the consultation to chat" : "Waiting for the lawyer to start…")}
                  data-testid="consult-input"
                  rows={1}
                  disabled={!isLive}
                  className="min-h-[44px] max-h-[120px] resize-none rounded-xl"
                />
                <Button onClick={sendMsg} disabled={!input.trim() || !isLive} data-testid="consult-send-btn" className="rounded-xl adv-btn-primary h-11 w-11 p-0">
                  <Send size={16} />
                </Button>
              </div>
            </div>
          )}
        </section>
      </div>

      {inCall && <DailyCall consultationId={consult.id} type={consult.consultation_type} lawyer={consult} onEnd={() => setInCall(false)} />}

      <RatingPromptModal
        consultationId={consult.id}
        lawyerName={consult.lawyer_name}
        open={ratingOpen}
        onOpenChange={(v) => {
          setRatingOpen(v);
          if (!v) localStorage.setItem(`adv_rated_${consult.id}`, "1");
        }}
        onSubmitted={() => { setHasReviewed(true); load(); }}
      />

      {user?.user_id === consult.user_id && consult.status === "completed" && !ratingOpen && (
        <button
          onClick={() => setRatingOpen(true)}
          data-testid="reopen-rating-btn"
          className="fixed bottom-6 right-6 rounded-full adv-btn-gold font-bold px-5 py-3 shadow-2xl flex items-center gap-2 text-sm z-40"
        >
          <Star size={14} fill="currentColor" /> {hasReviewed ? "Edit review" : "Rate consultation"}
        </button>
      )}
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex justify-between text-xs">
    <span className="text-slate-500">{k}</span>
    <span className="text-slate-800">{v}</span>
  </div>
);

function ChatSessionTimer({ durationMin, startedAt }) {
  const start = startedAt ? new Date(startedAt).getTime() : Date.now();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const totalSec = durationMin * 60;
  const elapsed = Math.floor((now - start) / 1000);
  const remaining = Math.max(0, totalSec - elapsed);
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  const warn = remaining <= 60;
  return (
    <div
      data-testid="chat-session-timer"
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${
        warn ? "bg-red-50 text-red-700" : "bg-slate-100 text-slate-700"
      }`}
      title={`Session: ${durationMin} minutes`}
    >
      <Clock size={11} /> {mm}:{ss}
    </div>
  );
}

function AiSummaryPanel({ cid, canEdit }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/consultations/${cid}/ai-summary`);
      setData(r.data);
      setDraft(r.data?.ai_summary || "");
    } catch { /* ignore */ }
  }, [cid]);
  useEffect(() => { load(); }, [load]);

  // Poll job status while a job is queued/running.
  useEffect(() => {
    if (!canEdit) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await api.get(`/consultations/${cid}/ai-summary/status`);
        if (cancelled) return;
        setJob(r.data?.job || null);
        if (r.data?.ai_summary) {
          setData((d) => ({ ...(d || {}), ai_summary: r.data.ai_summary, available: true,
            finalized: !!r.data.ai_summary_finalized }));
          setDraft(r.data.ai_summary);
        }
        if (r.data?.job?.state === "completed" || r.data?.job?.state === "failed") {
          setBusy(false);
        }
      } catch { /* ignore */ }
    };
    poll();
    const id = setInterval(() => {
      if (job && (job.state === "queued" || job.state === "running")) poll();
    }, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, [cid, canEdit, job?.state]);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/consultations/${cid}/ai-summary/enqueue`);
      setJob({ id: r.data.job_id, state: r.data.state });
      toast.success("Generation queued — this can take ~30-60s for GPT-5");
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); setBusy(false); }
  };

  const save = async (finalize = false) => {
    setBusy(true);
    try {
      const r = await api.patch(`/consultations/${cid}/ai-summary`, { ai_summary: draft, finalize });
      setData((d) => ({ ...(d || {}), ai_summary: r.data.ai_summary, finalized: r.data.ai_summary_finalized, available: true }));
      setEditing(false);
      toast.success(finalize ? "Summary shared with client" : "Draft saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const hasText = !!(data?.ai_summary);
  const isRunning = job && (job.state === "queued" || job.state === "running");
  if (!canEdit && !data?.available) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="ai-summary-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-[#D4AF37]" />
          <p className="text-sm font-bold text-[#0A2540]">AI Consultation Memo</p>
        </div>
        {data?.finalized && (
          <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid="ai-summary-finalized-pill">Shared with client</span>
        )}
      </div>

      {isRunning && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-800 flex items-center gap-2" data-testid="ai-summary-job-running">
          <Loader2 size={12} className="animate-spin" />
          {job.state === "queued" ? "Queued — starting GPT-5…" : "GPT-5 drafting your memo…"}
        </div>
      )}
      {job?.state === "failed" && (
        <p className="mt-2 text-xs text-rose-600" data-testid="ai-summary-job-error">Generation failed: {job.error || "try again"}</p>
      )}

      {!hasText && canEdit && !isRunning && (
        <div className="mt-3">
          <p className="text-xs text-slate-500 mb-2">Draft a polished memo from the chat transcript using GPT-5. Runs in the background — takes about 30-60 seconds.</p>
          <Button onClick={generate} disabled={busy} data-testid="ai-summary-generate-btn" className="w-full adv-btn-primary rounded-xl h-9">
            {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <FileText size={14} className="mr-1" />} Generate draft
          </Button>
        </div>
      )}

      {hasText && (
        <div className="mt-3 space-y-2">
          {editing ? (
            <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={12} className="text-xs rounded-xl font-mono" data-testid="ai-summary-textarea" />
          ) : (
            <pre className="text-xs whitespace-pre-wrap text-slate-700 max-h-72 overflow-y-auto bg-slate-50 rounded-xl p-3 border border-slate-100" data-testid="ai-summary-preview">{data.ai_summary}</pre>
          )}
          {canEdit && (
            <div className="flex flex-wrap gap-2">
              {!editing && (
                <Button onClick={() => setEditing(true)} variant="outline" className="rounded-xl text-xs h-8" data-testid="ai-summary-edit-btn">Edit</Button>
              )}
              {editing && (
                <>
                  <Button onClick={() => save(false)} disabled={busy} variant="outline" className="rounded-xl text-xs h-8" data-testid="ai-summary-save-btn">Save draft</Button>
                  <Button onClick={() => save(true)} disabled={busy} className="rounded-xl adv-btn-gold text-xs h-8" data-testid="ai-summary-finalize-btn">Share with client</Button>
                </>
              )}
              <Button onClick={generate} disabled={busy || isRunning} variant="outline" className="rounded-xl text-xs h-8" data-testid="ai-summary-regenerate-btn">Regenerate</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const Lock = (props) => <svg {...props} width={36} height={36} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>;
